import React, { useState, useRef, useEffect } from 'react';
import { Send, X } from 'lucide-react';
import { CHAT_PLACEHOLDER, CHAT_PLACEHOLDER_MOBILE } from '@/utils/constants';

interface ChatInputProps {
  onSendMessage: (message: string, options?: { use_async?: boolean }) => void;
  onCancelRequest?: () => void;
  isLoading: boolean;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onCancelRequest,
  isLoading,
  disabled = false,
  placeholder = CHAT_PLACEHOLDER,
  maxLength = 4000,
}) => {
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [responsivePlaceholder, setResponsivePlaceholder] =
    useState(placeholder);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (trimmed && !isLoading && !disabled) {
      onSendMessage(trimmed); // Always use SSE streaming
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      setMessage(value);
      // Auto-resize textarea
      const textarea = e.target;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  const handleCancel = () => {
    if (onCancelRequest) {
      onCancelRequest();
    }
  };

  // Focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Responsive placeholder: use shorter text on small screens
  useEffect(() => {
    // 639.98 not 640: Tailwind's `sm:` starts AT 640px, so a 640px viewport
    // would otherwise show the desktop header beside the mobile placeholder.
    const mq = window.matchMedia('(max-width: 639.98px)');
    const update = () =>
      setResponsivePlaceholder(
        mq.matches ? CHAT_PLACEHOLDER_MOBILE : placeholder,
      );
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, [placeholder]);

  const isMessageValid = message.trim().length > 0;

  return (
    <div className="border-t border-gray-200 bg-white p-4">
      <form onSubmit={handleSubmit} className="flex flex-col">
        {/* Input area */}
        <div
          className={`relative border rounded-lg transition-colors ${
            isFocused ? 'border-allianz-primary' : 'border-gray-300'
          } ${disabled ? 'opacity-50' : ''}`}
        >
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={responsivePlaceholder}
            disabled={disabled}
            className="w-full px-4 py-3 pr-14 sm:pr-32 content-end bg-transparent resize-none outline-none placeholder-gray-500 min-h-[50px] max-h-[120px]"
            rows={1}
          />

          {/* Press Enter to send text */}
          {!isLoading && (
            <div className="hidden sm:flex absolute top-1/2 right-14 transform -translate-y-1/2 items-center">
              <span className="text-xs text-gray-400 whitespace-nowrap">
                Press Enter to send
              </span>
            </div>
          )}

          {/* Send/Cancel button */}
          <div className="absolute bottom-3 right-3">
            {isLoading ? (
              <button
                type="button"
                onClick={handleCancel}
                className="p-2 text-gray-500 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors"
                title="Cancel request"
              >
                <X className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!isMessageValid || disabled}
                className={`p-2 rounded-full transition-colors ${
                  isMessageValid && !disabled
                    ? 'text-allianz-primary hover:bg-allianz-primary hover:text-white bg-gray-100'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                title="Send message (Enter)"
              >
                <Send className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};
